import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FolderOpen, Trash2, Pencil, Tags, Tag, ArrowRight, Search, X, ChevronDown, ChevronUp, CheckSquare, Square } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectsApi, keywordGroupsApi, serpApi, type Project, type KeywordGroup, type SerpRequest } from "@/api";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";

export default function KeywordGroups() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProject, setSelectedProject] = useState<string>(searchParams.get("project") || "__all__");
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<KeywordGroup | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [projectMultiFilter, setProjectMultiFilter] = useState<Set<string>>(new Set());
  const [tagMultiFilter, setTagMultiFilter] = useState<Set<string>>(new Set());
  // For create dialog project selector
  const [createProjectId, setCreateProjectId] = useState<string>("");
  // Tag input for new tags
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState("");

  // SERP quick-assign panel
  const [serpPanelOpen, setSerpPanelOpen] = useState(false);
  const [serpSearch, setSerpSearch] = useState("");
  const [serpSelected, setSerpSelected] = useState<Set<string>>(new Set());
  const [serpTargetGroup, setSerpTargetGroup] = useState("__none__");
  const [serpTagInput, setSerpTagInput] = useState("");
  const [serpTags, setSerpTags] = useState<string[]>([]);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Sync project selection to URL
  useEffect(() => {
    if (selectedProject && selectedProject !== "__all__") {
      setSearchParams({ project: selectedProject }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [selectedProject]);

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => (await projectsApi.list()).data || [],
  });

  // Fetch groups for all projects
  const groupQueries = useQueries({
    queries: projects.map(p => ({
      queryKey: ["keyword-groups", p.project_id],
      queryFn: async () => {
        const res = await keywordGroupsApi.list(p.project_id);
        return (res.data || []).map(g => ({ ...g, project_id: p.project_id }));
      },
    })),
  });

  // Fetch tags for all projects
  const tagQueries = useQueries({
    queries: projects.map(p => ({
      queryKey: ["project-tags", p.project_id],
      queryFn: async () => (await keywordGroupsApi.allTags(p.project_id)).data || [],
    })),
  });

  const isLoading = groupQueries.some(q => q.isLoading);

  // Combine all groups from all projects
  const allGroups = useMemo(() => {
    const groups: KeywordGroup[] = [];
    for (const q of groupQueries) {
      if (q.data) groups.push(...q.data);
    }
    return groups;
  }, [groupQueries]);

  // Combine all tags
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const q of tagQueries) {
      if (q.data) q.data.forEach((t: string) => tagSet.add(t));
    }
    return [...tagSet].sort();
  }, [tagQueries]);

  // Build project filter options
  const projectFilterOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const g of allGroups) {
      counts.set(g.project_id, (counts.get(g.project_id) || 0) + 1);
    }
    return projects
      .filter(p => counts.has(p.project_id))
      .map(p => ({ value: p.project_id, label: p.name, count: counts.get(p.project_id) || 0 }));
  }, [allGroups, projects]);

  // Build tag filter options
  const tagFilterOptions = useMemo(() => {
    return allTags.map(t => ({ value: t, label: t }));
  }, [allTags]);

  // Filter groups
  const filteredGroups = useMemo(() => {
    return allGroups.filter(g => {
      // Project filter
      if (projectMultiFilter.size > 0 && !projectMultiFilter.has(g.project_id)) return false;
      // Keyword/name search
      if (keywordFilter && !g.name.toLowerCase().includes(keywordFilter.toLowerCase())) return false;
      return true;
    });
  }, [allGroups, projectMultiFilter, keywordFilter]);

  // Helper to get project name
  const getProjectName = (projectId: string) => {
    return projects.find(p => p.project_id === projectId)?.name || projectId;
  };

  // SERP requests query (only when panel is open)
  const { data: serpRequests = [] } = useQuery<SerpRequest[]>({
    queryKey: ["serp-requests-all"],
    queryFn: async () => (await serpApi.requests({ limit: 500 })).data || [],
    enabled: serpPanelOpen,
  });

  // Unique keywords (completed only, deduplicated)
  const serpKeywords = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const r of serpRequests) {
      if (r.status === "completed" && r.target_keyword && !seen.has(r.target_keyword)) {
        seen.add(r.target_keyword);
        out.push(r.target_keyword);
      }
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [serpRequests]);

  const serpFiltered = useMemo(() =>
    serpSearch ? serpKeywords.filter(kw => kw.toLowerCase().includes(serpSearch.toLowerCase())) : serpKeywords
  , [serpKeywords, serpSearch]);

  const serpAllSelected = serpFiltered.length > 0 && serpFiltered.every(kw => serpSelected.has(kw));

  const toggleSerpAll = () => {
    if (serpAllSelected) {
      setSerpSelected(new Set());
    } else {
      setSerpSelected(new Set(serpFiltered));
    }
  };

  const toggleSerpOne = (kw: string) => {
    const next = new Set(serpSelected);
    if (next.has(kw)) next.delete(kw); else next.add(kw);
    setSerpSelected(next);
  };

  const addSerpTag = () => {
    const t = serpTagInput.trim();
    if (t && !serpTags.includes(t)) setSerpTags(prev => [...prev, t]);
    setSerpTagInput("");
  };

  const serpAssignMutation = useMutation({
    mutationFn: () => {
      const keywords = [...serpSelected];
      return keywordGroupsApi.addItems(serpTargetGroup, {
        keywords,
        tags: serpTags.length ? serpTags : undefined,
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["keyword-groups"] });
      toast.success(`已新增 ${res.data?.created || serpSelected.size} 個關鍵字`);
      setSerpSelected(new Set());
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const addNewTag = () => {
    const tag = newTagInput.trim();
    if (tag && !selectedTags.includes(tag)) {
      setSelectedTags(prev => [...prev, tag]);
    }
    setNewTagInput("");
  };

  const createMutation = useMutation({
    mutationFn: () => keywordGroupsApi.create({ project_id: createProjectId, name: name.trim(), description: description.trim() || undefined }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["keyword-groups"] });
      toast.success("群組已建立，前往新增關鍵字");
      setName(""); setDescription(""); setCreateOpen(false); setSelectedTags([]); setNewTagInput("");
      // Auto-navigate to new group
      if (res.data?.group_id) {
        navigate(`/keyword-groups/${res.data.group_id}`);
      }
    },
    onError: (err: Error) => toast.error(`建立失敗: ${err.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editGroup) return Promise.resolve();
      return keywordGroupsApi.update(editGroup.group_id, { name: name.trim(), description: description.trim() || undefined });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["keyword-groups"] });
      toast.success("群組已更新");
      setEditGroup(null); setName(""); setDescription("");
    },
    onError: (err: Error) => toast.error(`更新失敗: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (groupId: string) => keywordGroupsApi.delete(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["keyword-groups"] });
      toast.success("群組已刪除");
    },
    onError: (err: Error) => toast.error(`刪除失敗: ${err.message}`),
  });

  const openEdit = (g: KeywordGroup) => {
    setEditGroup(g);
    setName(g.name);
    setDescription(g.description || "");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">關鍵字群組</h1>
          <p className="text-muted-foreground">管理每個專案的關鍵字群組與標籤</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setName(""); setDescription(""); setCreateProjectId(projects[0]?.project_id || ""); setSelectedTags([]); setNewTagInput(""); }}>
              <Plus className="h-4 w-4 mr-2" />新增群組
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>新增關鍵字群組</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>專案</Label>
                <Select value={createProjectId} onValueChange={setCreateProjectId}>
                  <SelectTrigger><SelectValue placeholder="選擇專案..." /></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>群組名稱</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 24.4.12-isolation gowns" />
              </div>
              <div>
                <Label>說明（選填）</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="群組說明..." />
              </div>
              {/* Tags input */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Tag className="h-3 w-3" />Tags 標籤</Label>
                <div className="flex flex-wrap items-center gap-2 p-2 rounded-md border bg-muted/30 min-h-[36px]">
                  {selectedTags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs gap-1">
                      {tag}
                      <button onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="ml-0.5 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    className="h-7 w-[140px] border-none bg-transparent text-xs p-0 px-1 focus-visible:ring-0"
                    placeholder="+ 新增 Tag..."
                    value={newTagInput}
                    onChange={e => setNewTagInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && newTagInput.trim()) {
                        e.preventDefault();
                        addNewTag();
                      }
                    }}
                  />
                </div>
              </div>
              <Button onClick={() => createMutation.mutate()} disabled={!name.trim() || !createProjectId || createMutation.isPending}>
                {createMutation.isPending ? "建立中..." : "建立"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <MultiSelectFilter label="專案 Project" options={projectFilterOptions} selected={projectMultiFilter} onChange={setProjectMultiFilter} />
        <Input
          placeholder="搜尋群組名稱..."
          className="max-w-[240px] h-9"
          value={keywordFilter}
          onChange={e => setKeywordFilter(e.target.value)}
        />
        <MultiSelectFilter label="Tag 標籤" options={tagFilterOptions} selected={tagMultiFilter} onChange={setTagMultiFilter} />
        <span className="text-sm text-muted-foreground ml-auto">
          顯示 {filteredGroups.length} / {allGroups.length} 個群組
        </span>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editGroup} onOpenChange={open => { if (!open) setEditGroup(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>編輯群組</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>群組名稱</Label>
              <Input value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <Label>說明</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} />
            </div>
            <Button onClick={() => updateMutation.mutate()} disabled={!name.trim() || updateMutation.isPending}>
              {updateMutation.isPending ? "儲存中..." : "儲存"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Groups list */}
      {isLoading ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">載入中...</CardContent></Card>
      ) : filteredGroups.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          {allGroups.length === 0 ? "尚無關鍵字群組，點擊「新增群組」開始" : "無符合篩選條件的群組"}
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredGroups.map(g => (
            <Card key={g.group_id} className="hover:bg-muted/50 transition-colors cursor-pointer group"
              onClick={() => navigate(`/keyword-groups/${g.group_id}`)}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="flex items-center gap-2">
                  <Tags className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">{g.name}</CardTitle>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                    onClick={e => { e.stopPropagation(); openEdit(g); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                    onClick={e => { e.stopPropagation(); if (confirm(`確定刪除「${g.name}」？`)) deleteMutation.mutate(g.group_id); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {g.description && <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{g.description}</p>}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{g.keyword_count}</span>
                    <span className="text-muted-foreground">個關鍵字</span>
                  </div>
                  <Badge variant="outline" className="text-[11px]">{getProjectName(g.project_id)}</Badge>
                </div>
                <span className="text-xs text-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-2">
                  管理關鍵字 <ArrowRight className="h-3 w-3" />
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* SERP Keywords Quick-Assign Panel */}
      <Card className="mt-4">
        <CardHeader className="cursor-pointer select-none pb-3" onClick={() => setSerpPanelOpen(o => !o)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">SERP 關鍵字批次歸類</CardTitle>
              <Badge variant="secondary" className="text-xs">
                {serpKeywords.length > 0 ? `${serpKeywords.length} 個字詞` : "點擊載入"}
              </Badge>
            </div>
            {serpPanelOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
          <p className="text-xs text-muted-foreground mt-1">列出 SERP 已分析完成的字詞，批次勾選後快速加入群組 / 設定 TAG</p>
        </CardHeader>

        {serpPanelOpen && (
          <CardContent className="space-y-4">
            {/* Search + Select All */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜尋關鍵字..."
                  className="pl-8 h-9"
                  value={serpSearch}
                  onChange={e => setSerpSearch(e.target.value)}
                />
              </div>
              <Button variant="outline" size="sm" onClick={toggleSerpAll} className="gap-1">
                {serpAllSelected
                  ? <><CheckSquare className="h-4 w-4" />取消全選</>
                  : <><Square className="h-4 w-4" />全選 ({serpFiltered.length})</>}
              </Button>
              {serpSelected.size > 0 && (
                <Badge variant="default">{serpSelected.size} 個已選</Badge>
              )}
            </div>

            {/* Keyword list */}
            {serpFiltered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {serpKeywords.length === 0 ? "載入中..." : "無符合搜尋的字詞"}
              </p>
            ) : (
              <div className="border rounded-md divide-y max-h-[320px] overflow-y-auto">
                {serpFiltered.map(kw => (
                  <label key={kw} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer text-sm">
                    <Checkbox
                      checked={serpSelected.has(kw)}
                      onCheckedChange={() => toggleSerpOne(kw)}
                    />
                    <span>{kw}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Assign bar */}
            <div className="flex flex-wrap items-end gap-3 pt-1 border-t">
              {/* Target group */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">目標群組</label>
                <Select value={serpTargetGroup} onValueChange={setSerpTargetGroup}>
                  <SelectTrigger className="w-[220px] h-9">
                    <SelectValue placeholder="選擇群組..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— 選擇群組 —</SelectItem>
                    {allGroups.map(g => (
                      <SelectItem key={g.group_id} value={g.group_id}>
                        {g.name}
                        <span className="ml-1 text-muted-foreground text-xs">({getProjectName(g.project_id)})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tags */}
              <div className="space-y-1 flex-1 min-w-[200px]">
                <label className="text-xs text-muted-foreground">TAG 標籤（選填）</label>
                <div className="flex flex-wrap items-center gap-1.5 p-1.5 rounded-md border bg-background min-h-[36px]">
                  {serpTags.map(t => (
                    <Badge key={t} variant="outline" className="text-xs gap-1">
                      {t}
                      <button onClick={() => setSerpTags(prev => prev.filter(x => x !== t))} className="hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    className="h-6 flex-1 min-w-[80px] border-none bg-transparent text-xs p-0 px-1 focus-visible:ring-0"
                    placeholder="輸入 Tag 後按 Enter"
                    value={serpTagInput}
                    onChange={e => setSerpTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSerpTag(); } }}
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                disabled={serpSelected.size === 0 || serpTargetGroup === "__none__" || serpAssignMutation.isPending}
                onClick={() => serpAssignMutation.mutate()}
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                {serpAssignMutation.isPending ? "加入中..." : `加入群組 (${serpSelected.size})`}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
